import { Action } from '@ngrx/store';
import { Subject, of, throttleTime } from 'rxjs';

import { conformsTo, isArrayAndEveryElement, isString } from '@dmb/core';

import { logError } from '../../helper/log.helper';
import { UploadInfo } from '../../model/product/general';
import { UploadErrorType } from '../../model/product/wizard';
import { UploadActions } from './+state/app.actions';

/**
 * Functions to upload files to a user folder in our frontend cache s3 bucket.
 *
 * Support for single part and multi part uploads.
 * Multi part uploads can be interrupted and picked up again later.
 * Running uploads can be cancelled, finished uploads can be deleted.
 *
 * All requests are done with pre-sigend URLs, which are request from a dmb endpoint.
 *
 * Using XMLHttpRequest, instead of aws sdk, because we want to document progress.
 * Using SHA-1 for checksums, because it has browser support.
 */

// 100 MiB is best practice, according to aws user guide
const PART_SIZE_MULTI_PART_UPLOAD = 100 * 1024 * 1024;
export const MAX_SIZE_SINGLE_PART_UPLOAD = PART_SIZE_MULTI_PART_UPLOAD; // inclusive limit
const GENERAL_MAX_SIZE = 10 * 1024 * 1024 * 1024;
const MAX_ATTEMPTS = 5;

export function initializeUploadInfo(file: { size: number }): UploadInfo {
  return file.size > MAX_SIZE_SINGLE_PART_UPLOAD
    ? { uploadType: 'multi', status: 'running', finishedParts: [], uploadId: undefined, objectUri: undefined }
    : { uploadType: 'single', status: 'running' };
}

// base64 encoded sha1 checksum
export async function encodedChecksum(file: File) {
  if (!crypto.subtle) {
    return undefined;
  }

  const checksum = await crypto.subtle.digest('SHA-1', await file.arrayBuffer());
  const byteArray = new Uint8Array(checksum);

  if ('toBase64' in Uint8Array.prototype) {
    return (byteArray as unknown as { toBase64: () => string }).toBase64();
  } else {
    return btoa(String.fromCharCode(...byteArray));
  }
}

// NOTE: assumes only one request is running at any time
const runningRequests = new Map<string, { abort: (reason?: string) => void }>();

async function abortMultiUpload(file_uri: string, multipart_upload_id: string): Promise<boolean> {
  try {
    const bodyObj = { file_uri, multipart_upload_id };
    const response = await fetch('/api/v5/common/files/abort-multipart-upload', {
      method: 'POST',
      body: JSON.stringify(bodyObj),
      headers: { 'Content-Type': 'application/json' },
    });

    if (response.ok) {
      return true;
    }

    const err = new Error('Failed to abort multi part upload');
    logError(err, { extra: { response } });
    return false;
  } catch (cause) {
    const err = new Error('Failed to abort multi part upload', { cause });
    logError(err);

    return false;
  }
}

/**
 * Cancel upload, can not be picked back up.
 * Resolves true, if we know for sure that no object was created.
 * It's possible, that xhr.abort() succeeds, but the upload was already finished.
 */
export function cancelUpload(id: string, file_uri: string | undefined, uploadId: string | undefined) {
  const xhr = runningRequests.get(id);
  xhr?.abort('canceled');

  if (uploadId && file_uri) {
    return abortMultiUpload(file_uri, uploadId);
  }

  return Promise.resolve(false);
}

export function interruptAll() {
  for (const request of runningRequests.values()) {
    request.abort();
  }
}

export function singlePartUpload({
  sessionId,
  file,
  id,
  attempt,
}: {
  sessionId: string;
  file: File;
  id: string;
  attempt: number;
}) {
  const abortController = new AbortController();
  runningRequests.set(id, abortController);

  const xhr = new XMLHttpRequest();
  const actions = new Subject<Action>();
  const progressActions = new Subject<Action>();

  actions.subscribe({ complete: () => progressActions.complete() });
  progressActions.pipe(throttleTime(200, undefined, { trailing: true })).subscribe((action) => actions.next(action));

  let objectUri = '';

  xhr.upload.addEventListener('progress', (evt) => {
    if (!evt.lengthComputable) {
      return;
    }

    progressActions.next(UploadActions.progress({ sessionId, id, progress: evt.loaded / evt.total }));
  });

  xhr.addEventListener('loadend', () => {
    actions.complete();
    runningRequests.delete(id);
  });

  xhr.addEventListener('load', () => {
    if (xhr.status === 200) {
      actions.next(UploadActions.done({ sessionId, id, objectUri }));
    } else if (attempt > MAX_ATTEMPTS) {
      const err = new Error('Too many attempts');
      logError(err, { extra: { xhrResponseText: xhr.responseText } });

      actions.next(UploadActions.error({ sessionId, id, errorType: UploadErrorType.UNKNOWN }));
    } else {
      const errorCode = xhr.responseXML?.querySelector('Error > Code')?.textContent;
      switch (errorCode) {
        case 'ExpiredToken':
        case 'InvalidSignature':
        case 'InvalidToken':
        case 'SignatureDoesNotMatch':
        case 'BadDigest':
        case 'InvalidDigest':
        case 'InternalError':
          actions.next(UploadActions.start({ sessionId, uploads: [{ id, file }], attempt: attempt + 1 }));
          break;
        case 'SlowDown':
          actions.next(
            UploadActions.start({ sessionId, uploads: [{ id, file }], attempt: attempt + 1, backoff: true }),
          );
          break;
        default: {
          const err = new Error('Upload Error');
          logError(err, { extra: { xhrResponseText: xhr.responseText } });

          actions.next(UploadActions.error({ sessionId, id, errorType: UploadErrorType.UNKNOWN }));
          break;
        }
      }
    }
  });

  xhr.addEventListener('error', () => {
    const err = new Error('Failed to send XMLHttpRequest', { cause: xhr });
    logError(err, { extra: { xhr } });

    actions.next(UploadActions.error({ sessionId, id, errorType: UploadErrorType.UNKNOWN }));
  });

  const bodyObj = {
    file_name: file.name,
    file_size: file.size,
    content_type: file.type,
    multipart_part_size: PART_SIZE_MULTI_PART_UPLOAD,
  };

  fetch('/api/v5/common/files/init-upload', {
    method: 'POST',
    body: JSON.stringify(bodyObj),
    headers: { 'Content-Type': 'application/json' },
    signal: abortController.signal,
  })
    .then((response) => response.json())
    .then((responseJson) => {
      if (abortController.signal.aborted) {
        runningRequests.delete(id);
        actions.complete();
        return;
      }

      if (!conformsTo({ file_uri: isString, single_upload_url: isString })(responseJson)) {
        const err = new Error('Received invalid result from init-upload');
        logError(err, { extra: { response: responseJson } });

        runningRequests.delete(id);
        actions.next(UploadActions.error({ sessionId, id, errorType: UploadErrorType.UNKNOWN }));
        actions.complete();
        return;
      }

      objectUri = responseJson.file_uri;
      runningRequests.set(id, xhr);
      xhr.open('PUT', responseJson.single_upload_url);
      xhr.send(file);
    })
    .catch((err) => {
      runningRequests.delete(id);
      if (!abortController.signal.aborted) {
        logError(err);
        actions.next(UploadActions.error({ sessionId, id, errorType: UploadErrorType.UNKNOWN }));
      }
      actions.complete();
    });

  return actions;
}

function uploadPart({
  sessionId,
  id,
  file,
  part,
  partNumber,
  url,
  actions,
  progressActions,
  attempt,
  uploadId,
  objectUri,
  multipartUrls,
  finishedParts,
  abortSignal,
}: {
  sessionId: string;
  id: string;
  file: File;
  part: Blob;
  partNumber: number;
  url: string;
  actions: Subject<Action>;
  progressActions: Subject<Action>;
  attempt: number;
  uploadId: string;
  objectUri: string;
  multipartUrls: string[];
  finishedParts: { partNumber: number; etag?: string }[];
  abortSignal: AbortSignal;
}) {
  return new Promise<{ partNumber: number; etag?: string }>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const boundAbort = xhr.abort.bind(xhr);
    abortSignal.addEventListener('abort', boundAbort);

    xhr.addEventListener('loadend', () => {
      abortSignal.removeEventListener('abort', boundAbort);
    });

    xhr.upload.addEventListener('progress', (evt) => {
      let soFar = (partNumber - 1) * PART_SIZE_MULTI_PART_UPLOAD;

      if (evt.lengthComputable) {
        soFar += evt.loaded;
      }

      progressActions.next(UploadActions.progress({ sessionId, id, progress: soFar / file.size }));
    });

    xhr.addEventListener('load', () => {
      if (xhr.status === 200) {
        const etag = xhr.getResponseHeader('ETag') ?? undefined;

        actions.next(UploadActions.partDone({ sessionId, id, etag, partNumber }));

        resolve({ etag, partNumber });
      } else if (attempt > MAX_ATTEMPTS) {
        const err = new Error('Too many attempts');
        logError(err, { extra: { xhrResponseText: xhr.responseText } });

        reject(UploadActions.error({ sessionId, id, errorType: UploadErrorType.UNKNOWN }));
      } else {
        const errorCode = xhr.responseXML?.querySelector('Error > Code')?.textContent;
        switch (errorCode) {
          case 'ExpiredToken':
          case 'InvalidSignature':
          case 'InvalidToken':
          case 'SignatureDoesNotMatch':
          case 'BadDigest':
          case 'InvalidDigest':
          case 'InternalError':
            reject(
              UploadActions.start({
                sessionId,
                uploads: [
                  {
                    id,
                    file,
                    finishedParts,
                  },
                ],
                attempt: attempt + 1,
              }),
            );
            break;
          case 'NoSuchUpload':
            reject(
              UploadActions.start({
                sessionId,
                uploads: [
                  {
                    id,
                    file,
                  },
                ],
                attempt: attempt + 1,
              }),
            );
            break;
          case 'SlowDown':
            reject(
              UploadActions.start({
                sessionId,
                uploads: [
                  {
                    id,
                    file,
                    uploadId,
                    objectUri,
                    multipartUrls,
                    finishedParts,
                  },
                ],
                attempt: attempt + 1,
                backoff: true,
              }),
            );
            break;
          default: {
            const err = new Error('Upload Error');
            logError(err, { extra: { xhrResponseText: xhr.responseText } });

            reject(UploadActions.error({ sessionId, id, errorType: UploadErrorType.UNKNOWN }));
            break;
          }
        }
      }
    });

    xhr.addEventListener('error', () => {
      const err = new Error('Failed to send XMLHttpRequest');
      logError(err, { extra: { xhr } });

      reject(UploadActions.error({ sessionId, id, errorType: UploadErrorType.UNKNOWN }));
    });

    xhr.addEventListener('abort', () => {
      reject(null);
    });

    xhr.open('PUT', url);

    xhr.send(part);
  });
}

export function multiPartUpload({
  sessionId,
  file,
  id,
  uploadId,
  objectUri,
  multipartUrls,
  finishedParts: finishedPartsParam,
  attempt,
}: {
  sessionId: string;
  file: File;
  id: string;
  uploadId?: string;
  objectUri?: string;
  multipartUrls?: string[];
  finishedParts?: { partNumber: number; etag?: string }[];
  attempt: number;
}) {
  if (file.size > GENERAL_MAX_SIZE) {
    return of(UploadActions.error({ sessionId, id, errorType: UploadErrorType.FILE_TOO_BIG }));
  }

  const highestAlreadyFinishedPartNumber = finishedPartsParam?.[finishedPartsParam.length - 1]?.partNumber || 0;
  const finishedParts = finishedPartsParam?.slice() ?? [];
  const actions = new Subject<Action>();
  const progressActions = new Subject<Action>();

  actions.subscribe({ complete: () => progressActions.complete() });
  progressActions.pipe(throttleTime(200, undefined, { trailing: true })).subscribe((action) => actions.next(action));

  const abortController = new AbortController();
  runningRequests.set(id, abortController);

  const bodyObj = {
    file_name: file.name,
    file_size: file.size,
    content_type: file.type,
    multipart_part_size: PART_SIZE_MULTI_PART_UPLOAD,
  };

  const checksumPromise = encodedChecksum(file);

  const initResponsePromise =
    uploadId && objectUri && multipartUrls
      ? Promise.resolve({ file_uri: objectUri, multipart_upload_id: uploadId, multipart_upload_urls: multipartUrls })
      : fetch('/api/v5/common/files/init-upload', {
          method: 'POST',
          body: JSON.stringify(bodyObj),
          headers: { 'Content-Type': 'application/json' },
          signal: abortController.signal,
        })
          .then((initResponse) => initResponse.json())
          .then(async (initResponse) => {
            if (
              !conformsTo({
                file_uri: isString,
                multipart_upload_id: isString,
                multipart_upload_urls: isArrayAndEveryElement(isString),
              })(initResponse)
            ) {
              throw new Error('Received invalid result from init-upload', { cause: initResponse });
            }

            return initResponse;
          });

  initResponsePromise
    .then(async ({ multipart_upload_id: uploadId, file_uri: objectUri, multipart_upload_urls: multipartUrls }) => {
      actions.next(UploadActions.multiCreated({ sessionId, id, uploadId, objectUri }));

      if (abortController.signal.aborted) {
        return;
      }

      const parts: Blob[] = [];

      for (
        let offset = highestAlreadyFinishedPartNumber * PART_SIZE_MULTI_PART_UPLOAD;
        offset < file.size;
        offset += PART_SIZE_MULTI_PART_UPLOAD
      ) {
        parts.push(file.slice(offset, offset + PART_SIZE_MULTI_PART_UPLOAD));
      }

      for (let i = 0; i < parts.length; i++) {
        const partNumber = i + 1 + highestAlreadyFinishedPartNumber;

        finishedParts.push(
          await uploadPart({
            sessionId,
            id,
            file,
            part: parts[i],
            partNumber,
            uploadId,
            objectUri,
            multipartUrls,
            attempt,
            actions,
            progressActions,
            finishedParts,
            url: multipartUrls[partNumber - 1],
            abortSignal: abortController.signal,
          }),
        );
      }

      actions.next(UploadActions.allPartsDone({ sessionId, id }));

      const bodyObj = {
        file_uri: objectUri,
        multipart_upload_id: uploadId,
        multipart_part_etags: finishedParts.map((p) => p.etag),
        checksum_sha1: await checksumPromise,
      };

      const response = await fetch('/api/v5/common/files/complete-multipart-upload', {
        method: 'POST',
        body: JSON.stringify(bodyObj),
        headers: { 'Content-Type': 'application/json' },
        signal: abortController.signal,
      });

      if (response.ok) {
        actions.next(UploadActions.done({ sessionId, id, objectUri }));
      } else {
        actions.next(UploadActions.error({ sessionId, id, errorType: UploadErrorType.UNKNOWN }));
      }
    })
    .catch((errorAction) => {
      if (abortController.signal.aborted) {
        return;
      }

      if (errorAction instanceof Error) {
        if (errorAction.cause) {
          logError(errorAction, { extra: { cause: errorAction.cause } });
        } else {
          logError(errorAction);
        }

        actions.next(UploadActions.error({ sessionId, id, errorType: UploadErrorType.UNKNOWN }));
      } else if (errorAction !== null) {
        actions.next(errorAction);
      }
    })
    .finally(() => {
      actions.complete();
    });

  return actions;
}

export function errorMessageFor(errorType: UploadErrorType | undefined | null): string[] | null {
  switch (errorType) {
    case UploadErrorType.UNKNOWN:
      return [$localize`:@@product-wizard.upload-error.unknown:Upload failed`];
    case UploadErrorType.FILE_TOO_BIG:
      return [$localize`:@@product-wizard.upload-error.file-too-big:The given file is too big`];
    default:
      return null;
  }
}
